import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const LINKS = [
  { label: "Legacy", href: "#legacy", testId: "nav-link-legacy" },
  { label: "New Yachts", href: "/yachts", testId: "nav-link-yachts" },
  { label: "Brand Partners", href: "#brands", testId: "nav-link-brands" },
  { label: "After Sales", href: "#after-sales", testId: "nav-link-after-sales" },
  { label: "Contact", href: "#contact", testId: "nav-link-contact" },
];

export const scrollToSection = (href, immediate = false) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -72, duration: 1.6, immediate });
  } else {
    el.scrollIntoView({ behavior: immediate ? "auto" : "smooth" });
  }
};

export const requestEnquiry = (interest) => {
  sessionStorage.setItem("mm_interest", interest);
  window.dispatchEvent(new CustomEvent("mm-interest", { detail: interest }));
};

export const useSiteNav = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (href, extra = {}) => {
    if (extra.interest) requestEnquiry(extra.interest);
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    if (pathname === "/") {
      scrollToSection(href);
      return;
    }
    navigate("/", { state: { scrollTo: href } });
  };
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const goTo = useSiteNav();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    goTo(href);
  };

  const isActive = (href) => href.startsWith("/") && pathname.startsWith(href);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 border-b backdrop-blur-xl backdrop-saturate-150 transition-colors duration-500 ${
        scrolled || pathname !== "/" ? "bg-navy/95 border-navy-ink/10" : "bg-navy/85 border-white/40 shadow-[0_1px_0_rgba(255,255,255,0.35)]"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
        <a
          href="/"
          onClick={(e) => go(e, pathname === "/" ? "#hero" : "/")}
          className="flex flex-col leading-none"
          data-testid="nav-brand"
        >
          <img src="/modi-marine-logo.png" alt="Modi Marine" className="h-[18px] w-auto sm:h-5" data-testid="nav-logo" />
        </a>

        <nav className="hidden items-center gap-10 lg:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => go(e, l.href)}
              data-testid={l.testId}
              className={`text-[11px] uppercase tracking-[0.28em] transition-colors duration-300 hover:text-gold ${
                isActive(l.href) ? "text-gold" : "text-mist"
              }`}
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/enquire"
            data-testid="nav-enquire-button"
            className="border border-gold/60 px-6 py-2.5 text-[11px] uppercase tracking-[0.28em] text-gold transition-colors duration-300 hover:bg-gold hover:text-navy"
          >
            Enquire
          </Link>
        </nav>

        <button
          className="text-navy-ink lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <motion.nav
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="border-t border-navy-ink/10 bg-navy/95 px-6 py-6 backdrop-blur-xl lg:hidden"
          data-testid="nav-mobile-menu"
        >
          <div className="flex flex-col gap-5">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                data-testid={`${l.testId}-mobile`}
                className="text-xs uppercase tracking-[0.3em] text-mist transition-colors duration-300 hover:text-gold"
              >
                {l.label}
              </a>
            ))}
            <Link to="/enquire" onClick={() => setOpen(false)} data-testid="nav-enquire-button-mobile" className="text-xs uppercase tracking-[0.3em] text-gold">
              Enquire
            </Link>
          </div>
        </motion.nav>
      )}
    </motion.header>
  );
}
